// src/components/onboarding/OnboardingModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient'; 
import { X, Send, ChevronRight, ChevronLeft, User, Briefcase, Brain, Settings, CheckCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

// Helper function to convert to Title Case (useful for dynamic tags)
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const OnboardingModal = ({ isOpen, onClose }) => {
  const { user, profile, updateProfileAndCompleteOnboarding, fetchUserProfile } = useAuth();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  
  const [selectedUserType, setSelectedUserType] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedContentPreference, setSelectedContentPreference] = useState('');
  
  const [loading, setLoading] = useState(false); // For the final submit
  const [error, setError] = useState('');

  // State for DB-driven configuration
  const [userTypesFromDB, setUserTypesFromDB] = useState([]);
  const [contentPreferencesFromDB, setContentPreferencesFromDB] = useState([]);
  const [baseInterests, setBaseInterests] = useState([]); 
  const [combinedInterests, setCombinedInterests] = useState([]);
  const [configLoading, setConfigLoading] = useState(true); 
  const [dynamicInterestsLoading, setDynamicInterestsLoading] = useState(false);

  // Effect to populate component state from the user's existing profile
  useEffect(() => {
    if (profile) {
      setSelectedUserType(profile.user_type || '');
      setSelectedInterests(profile.ai_interests || []);
      setSelectedContentPreference(profile.content_preferences?.summary_style || '');
    } else if (user && isOpen) {
      fetchUserProfile(user);
    }
  }, [profile, user, isOpen, fetchUserProfile]);

  // Effect to fetch ALL initial onboarding configurations from DB
  useEffect(() => {
    const fetchOnboardingConfigs = async () => {
      if (!isOpen) return; // Only fetch if modal is open
      console.log("OnboardingModal: Fetching initial configurations...");
      setConfigLoading(true);
      try {
        const [userTypesRes, contentPrefsRes, baseInterestsRes] = await Promise.all([
          supabase.from('config_user_types').select('*').eq('is_enabled', true).order('display_order'),
          supabase.from('config_content_preferences').select('*').eq('is_enabled', true).order('display_order'),
          supabase.from('config_onboarding_interests').select('interest_text').eq('is_enabled', true).order('display_order')
        ]);

        if (userTypesRes.error) throw userTypesRes.error;
        if (contentPrefsRes.error) throw contentPrefsRes.error;
        if (baseInterestsRes.error) throw baseInterestsRes.error;

        setUserTypesFromDB(userTypesRes.data || []);
        setContentPreferencesFromDB(contentPrefsRes.data || []);
        
        const interests = baseInterestsRes.data ? baseInterestsRes.data.map(item => item.interest_text) : [];
        setBaseInterests(interests);
        setCombinedInterests(interests); // Initialize combined list with base interests
        console.log("OnboardingModal: Base interests loaded:", interests);

      } catch (err) {
        console.error("OnboardingModal: Failed to fetch onboarding configurations:", err);
        setError("Could not load personalization options. Please try again later.");
        setUserTypesFromDB([]);
        setContentPreferencesFromDB([]);
        setBaseInterests([]);
        setCombinedInterests([]); // Ensure it's empty if base fails
      } finally {
        setConfigLoading(false);
      }
    };

    fetchOnboardingConfigs();
  }, [isOpen]); // Fetch when modal becomes visible

  // Effect to fetch DYNAMIC interests (trending) for step 2
  useEffect(() => {
    const fetchDynamicInterests = async () => {
      console.log("OnboardingModal: Step 2 active, attempting to fetch dynamic interests.");
      setDynamicInterestsLoading(true);
      try {
        const { data: dynamicTagsFromServer, error: funcError } = await supabase.functions.invoke('get-onboarding-interests');
        
        console.log("OnboardingModal: Dynamic tags from server raw response:", dynamicTagsFromServer);
        if (funcError) {
            console.error("OnboardingModal: Error invoking 'get-onboarding-interests':", funcError.message);
            throw funcError; // Let catch block handle it
        }

        const finalInterestsMap = new Map();
        
        // 1. Add base interests first, establishing their casing as preferred.
        //    Base interests are from config_onboarding_interests and should have desired display casing.
        (baseInterests || []).forEach(interest => {
          finalInterestsMap.set(interest.toLowerCase().trim(), interest);
        });
        console.log("OnboardingModal: Map after adding base interests:", new Map(finalInterestsMap));


        // 2. Add dynamic tags (these are canonical & lowercase from backend after synonym/generic filtering)
        //    Only add if the canonical form isn't already represented by a baseInterest.
        //    If it is new, use toTitleCase for display.
        if (dynamicTagsFromServer && Array.isArray(dynamicTagsFromServer)) {
          console.log("OnboardingModal: Dynamic tags fetched:", dynamicTagsFromServer);
          dynamicTagsFromServer.forEach(tag => {
            if (typeof tag === 'string' && tag.trim() !== "") {
              const lowerTag = tag.toLowerCase().trim(); // Should already be canonical lowercase
              if (!finalInterestsMap.has(lowerTag)) {
                finalInterestsMap.set(lowerTag, toTitleCase(tag)); 
              }
            }
          });
        } else {
          console.warn("OnboardingModal: Dynamic interests from server was not a valid array.");
        }
        
        const uniqueDisplayInterests = Array.from(finalInterestsMap.values());
        setCombinedInterests(uniqueDisplayInterests.slice(0, 20)); // Limit total displayed
        console.log("OnboardingModal: Final combined interests for display:", uniqueDisplayInterests.slice(0, 20));
        
      } catch (err) {
        console.error("OnboardingModal: Exception fetching/processing dynamic interests:", err.message);
        // If dynamic fetch fails, combinedInterests will retain the baseInterests (or what was set before)
        // To be safe, ensure it's at least baseInterests if dynamic fails
        setCombinedInterests(prevCombined => (prevCombined && prevCombined.length > 0 && prevCombined.every(item => baseInterests.includes(item))) ? prevCombined : [...baseInterests]);
      } finally {
        setDynamicInterestsLoading(false);
        console.log("OnboardingModal: Finished fetching dynamic interests.");
      }
    };

    // Fetch dynamic interests if on step 2, modal is open, AND initial configs are done loading.
    if (isOpen && step === 2 && !configLoading) { 
      fetchDynamicInterests();
    }
  }, [isOpen, step, baseInterests, configLoading]); // Rerun if baseInterests or configLoading status changes while on step 2

  
  // Handlers (handleNext, handlePrev, handleInterestToggle, handleSubmit)
  const handleNext = () => {
    // ... (validation logic from your file)
    if (step === 1 && !selectedUserType) {
      setError('Please select your primary role.'); 
      return;
    }
    if (step === 2 && selectedInterests.length === 0) {
      setError('Please select at least one AI interest.'); 
      return;
    }
    if (step === 3 && !selectedContentPreference) {
      setError('Please select your content preference.'); 
      return;
    }
    setError('');
    setStep(prev => prev + 1); 
  };

  const handlePrev = () => setStep(prev => prev - 1); 

  const handleInterestToggle = (interest) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    ); 
  };
  
  const handleSubmit = async () => {
    if (!selectedUserType || selectedInterests.length === 0 || !selectedContentPreference) {
        setError('Please complete all steps.'); 
        return;
    }
    setLoading(true);
    setError('');
    const profileUpdates = {
      user_type: selectedUserType,
      ai_interests: selectedInterests,
      content_preferences: { 
        summary_style: selectedContentPreference, 
        show_technical_details: selectedUserType === 'technical' 
      },
    };

    try {
      // Optimistically update the profile data
      queryClient.setQueryData(['userProfile', user?.id], (oldData) => {
        if (!oldData) return oldData;
        
        console.log('Optimistically updating profile after onboarding');
        return {
          ...oldData,
          ...profileUpdates
        };
      });

    const success = await updateProfileAndCompleteOnboarding(profileUpdates); 
    if (success) {
        // Set a flag in sessionStorage to indicate this is a new user who just completed onboarding
        sessionStorage.setItem('justCompletedOnboarding', 'true');
        
        // Add a delay before proceeding to step 5
        setTimeout(() => {
      setStep(5); 
          console.log('Onboarding completed successfully, profile updated');
        }, 1000);
    } else {
      setError('Failed to save preferences. Please try again.'); 
    }
    } catch (err) {
      console.error('Error during onboarding completion:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
    setLoading(false);
    }
  };

  const renderStepContent = () => {
    // Initial config loading or critical error for steps 1-3
    if (configLoading && step <= 3 && step !== 0) { 
      return <div className="text-center p-10">Loading personalization options...</div>;
    }
    
    if (error && step < 4 && ((step === 1 && userTypesFromDB.length === 0) || (step === 3 && contentPreferencesFromDB.length === 0) || (step === 2 && baseInterests.length === 0 && combinedInterests.length === 0 && !dynamicInterestsLoading))) {
      return <p className="text-red-500 text-sm my-4 text-center">{error}</p>;
    }
    
    // Step specific validation error (local to the step, not config load error)
    let stepValidationError = '';
    if (error) {
        if (step === 1 && !selectedUserType) stepValidationError = error;
        else if (step === 2 && selectedInterests.length === 0) stepValidationError = error;
        else if (step === 3 && !selectedContentPreference) stepValidationError = error;
        else if (step === 4) stepValidationError = error; // For final submission error
    }

    switch (step) {
      case 1:
        return (
          <div>
            <User size={48} className="mx-auto text-accent-teal mb-4" />
            <h2 className="text-2xl font-bold text-heading-text mb-2 text-center">Welcome to AI Brahmi!</h2>
            <p className="text-center text-main-text mb-6">Let's personalize your AI information experience.</p>
            {stepValidationError && <p className="text-red-500 text-sm mb-4 text-center">{stepValidationError}</p>}
            <div className="space-y-3">
              <p className="font-medium text-main-text">What's your primary role or interest?</p>
              {userTypesFromDB.map(type => (
                <button
                  key={type.id}
                  onClick={() => { setError(''); setSelectedUserType(type.id); }}
                  className={`w-full text-left p-3 border rounded-md transition-colors ${selectedUserType === type.id ? 'bg-accent-teal text-white border-accent-teal' : 'bg-card-bg border-gray-300 hover:border-accent-teal'}`}
                >
                  <p className="font-semibold">{type.label}</p>
                  <p className="text-xs opacity-80">{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <Brain size={48} className="mx-auto text-accent-teal mb-4" />
            <h2 className="text-2xl font-bold text-heading-text mb-6 text-center">What AI topics excite you?</h2>
            {stepValidationError && <p className="text-red-500 text-sm mb-4 text-center">{stepValidationError}</p>}
            <p className="text-sm text-main-text mb-4 text-center">Select all that apply. This helps us curate relevant content for you.</p>
            {dynamicInterestsLoading && <p className="text-center text-main-text my-2">Loading latest trends...</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-60 overflow-y-auto pr-2">
              {combinedInterests.map(interest => (
                <button
                  key={interest}
                  onClick={() => { setError(''); handleInterestToggle(interest);}}
                  className={`p-2 border rounded-md text-sm transition-colors ${selectedInterests.includes(interest) ? 'bg-accent-teal text-white border-accent-teal' : 'bg-card-bg border-gray-300 hover:border-accent-teal'}`}
                >
                  {interest}
                </button>
              ))}
              {combinedInterests.length === 0 && !dynamicInterestsLoading && !configLoading && <p className="col-span-full text-center text-main-text/70">No interest suggestions available at the moment. Please check back later or proceed.</p>}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <Settings size={48} className="mx-auto text-accent-teal mb-4" />
            <h2 className="text-2xl font-bold text-heading-text mb-6 text-center">How do you prefer your insights?</h2>
            {stepValidationError && <p className="text-red-500 text-sm mb-4 text-center">{stepValidationError}</p>}
            <div className="space-y-3">
              {contentPreferencesFromDB.map(pref => (
                <button
                  key={pref.id}
                  onClick={() => { setError(''); setSelectedContentPreference(pref.id);}}
                  className={`w-full text-left p-3 border rounded-md transition-colors ${selectedContentPreference === pref.id ? 'bg-accent-teal text-white border-accent-teal' : 'bg-card-bg border-gray-300 hover:border-accent-teal'}`}
                >
                  <p className="font-semibold">{pref.label}</p>
                  <p className="text-xs opacity-80">{pref.description}</p>
                </button>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
         <div>
           <Briefcase size={48} className="mx-auto text-accent-teal mb-4" />
           <h2 className="text-2xl font-bold text-heading-text mb-6 text-center">Ready to Go?</h2>
           {stepValidationError && <p className="text-red-500 text-sm mb-4 text-center">{stepValidationError}</p>}
           <div className="space-y-2 text-sm bg-slate-50 p-4 rounded-md">
             <p><strong className="text-heading-text">Your Role:</strong> {userTypesFromDB.find(ut => ut.id === selectedUserType)?.label || 'Not set'}</p>
             <p><strong className="text-heading-text">Interests:</strong> {selectedInterests.join(', ') || 'None selected'}</p>
             <p><strong className="text-heading-text">Preference:</strong> {contentPreferencesFromDB.find(cp => cp.id === selectedContentPreference)?.label || 'Not set'}</p>
           </div>
           <p className="text-xs text-main-text mt-4 text-center">You can always update these later in your profile.</p>
         </div>
       );
      case 5:
        return (
            <div className="text-center">
                <CheckCircle size={60} className="mx-auto text-green-500 mb-4" />
                <h2 className="text-2xl font-bold text-heading-text mb-2">All Set!</h2>
                <p className="text-main-text mb-6">Your AI Nexus experience is now personalized. Dive in!</p>
                <button
              onClick={() => {
                console.log('Completing onboarding');
                // Close modal immediately
                onClose();
                // Simple page reload - the most reliable method
                window.location.reload();
              }}
                    className="w-full bg-accent-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition duration-150 flex items-center justify-center"
                >
                    Explore AI Nexus
                </button>
            </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h1 className="text-2xl font-bold text-heading-text mb-6 text-center">Personalize Your Experience</h1>
        <div className="flex flex-col">
          <div className="overflow-y-auto max-h-[60vh]">
         {renderStepContent()}
        </div>
          <div className="mt-6 flex justify-between">
            {step > 1 && (
              <button
                onClick={handlePrev}
                className="bg-card-bg text-main-text py-2 px-4 rounded-md hover:bg-accent-teal hover:text-white transition duration-150"
              >
                Previous
                </button>
            )}
            {step < 5 && (
              <button
                onClick={step === 4 ? handleSubmit : handleNext}
                disabled={loading}
                className={`bg-accent-teal text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition duration-150 ${loading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {step === 4 ? (loading ? 'Saving...' : 'Complete') : 'Next'}
                </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;